import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { message } from 'antd';
import API_URL from '../../config/api';
import { apiFetch } from '../../utils/apiFetch';
import { getAudience } from '../../config/permissions';
import { SYSTEM_PAGE_CSS, LANDLORD_ROLE_PERMISSIONS, staffInvitePreview } from '../landlord/systemPageStyles';
import { ListPagination, paginateList } from '../../components/ListPagination';
import '../../styles/listPage.css';

interface StaffMember {
  id: string;
  name: string;
  email: string;
  phone: string;
  role: string;
  hourly_rate: number;
  working_hours: any;
  skills: string[] | null;
  active: boolean;
  photo_url?: string;
  status?: string;
}

const DAYS = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];
const DAY_INIT = DAYS.reduce<Record<string,{active:boolean;start:string;end:string}>>(
  (acc, d) => ({ ...acc, [d]: { active: !['saturday','sunday'].includes(d), start: '09:00', end: '17:00' } }), {}
);

const ROLE_COLOR: Record<string,{bg:string;color:string}> = {
  Admin:   { bg: '#fef2f2', color: '#dc2626' },
  Staff:   { bg: '#eff6ff', color: '#2563eb' },
  Editor:  { bg: '#f0fdf4', color: '#16a34a' },
  Viewer:  { bg: '#fafafa', color: '#6b7280' },
  Manager: { bg: '#fdf4ff', color: '#9333ea' },
};
const roleStyle = (r: string) => ROLE_COLOR[r] || { bg: '#f9fafb', color: '#374151' };

const emptyForm = {
  name: '', email: '', phone: '', role: 'Staff', hourly_rate: 0,
  skills: '', active: true, working_hours: DAY_INIT
};

const StaffManagement: React.FC = () => {
  const { token, role, userName } = useAuth();
  const canManageStaff = role === 'Admin' || role === 'Manager';
  const isAdmin = role === 'Admin';
  const isLandlord = getAudience(localStorage.getItem('business_type')) === 'landlord';

  const [staff, setStaff]           = useState<StaffMember[]>([]);
  const [businessName, setBusinessName] = useState('Your business');
  const [businessEmail, setBusinessEmail] = useState('');
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [listPage, setListPage] = useState(1);
  const [showModal, setShowModal]   = useState(false);
  const [editId, setEditId]         = useState<string|null>(null);
  const [form, setForm]             = useState({ ...emptyForm });
  const [saving, setSaving]         = useState(false);
  const [photoBase64, setPhotoBase64] = useState<string | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);

  useEffect(() => { fetchStaff(); }, [token]);

  useEffect(() => {
    setListPage(1);
  }, [search, roleFilter, statusFilter]);

  useEffect(() => {
    if (!token || !isLandlord) return;
    apiFetch(`${API_URL}/business`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d?.name) setBusinessName(d.name);
        if (d?.email) setBusinessEmail(d.email);
      })
      .catch(() => undefined);
  }, [token, isLandlord]);

  const fetchStaff = async () => {
    setLoading(true);
    try {
      const r = await apiFetch(`${API_URL}/staff`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (r.ok) {
        const d = await r.json();
        const rows = Array.isArray(d) ? d : (d.rows || []);
        setStaff(rows.map((s: any) => ({
          ...s,
          active: s.active !== undefined ? s.active : (s.isActive !== undefined ? s.isActive : true),
          hourly_rate: s.hourly_rate || s.hourlyRate || 0,
          working_hours: s.working_hours || s.workingHours || DAY_INIT,
          skills: s.skills || [],
          photo_url: s.photo_url || s.photoUrl || '',
          status: s.status
        })));
      }
    } catch { message.error('Failed to load staff'); }
    finally { setLoading(false); }
  };

  const openCreate = () => {
    setEditId(null);
    setForm({ ...emptyForm });
    setPhotoBase64(null);
    setPhotoPreview(null);
    setShowModal(true);
  };
  const openEdit = (s: StaffMember) => {
    setEditId(s.id);
    setPhotoBase64(null);
    setPhotoPreview(s.photo_url || null);
    setForm({
      name: s.name, email: s.email, phone: s.phone || '', role: s.role,
      hourly_rate: s.hourly_rate, active: s.active,
      skills: (s.skills || []).join(', '),
      working_hours: (s.working_hours && Object.keys(s.working_hours).length > 0) ? s.working_hours : DAY_INIT
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.email.trim()) { message.error('Name and email are required'); return; }
    setSaving(true);
    try {
      const payload: any = {
        name: form.name,
        email: form.email,
        phone: form.phone,
        role: form.role,
        hourlyRate: form.hourly_rate,
        skills: form.skills.split(',').map(s => s.trim()).filter(Boolean),
        workingHours: form.working_hours,
        isActive: form.active
      };
      if (photoBase64) payload.photoBase64 = photoBase64;
      const url  = editId ? `${API_URL}/staff/${editId}` : `${API_URL}/staff`;
      const meth = editId ? 'PUT' : 'POST';
      // Debug: log before API call
      console.log('Saving staff:', { url, method: meth, payload });
      const r = await apiFetch(url, {
        method: meth,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      // Debug: log after response
      console.log('Save response status:', r.status);
      const responseText = await r.text();
      console.log('Save response body:', responseText);
      if (r.ok) {
        message.success(editId ? 'Staff updated!' : 'Staff member added!');
        setShowModal(false);
        fetchStaff();
      } else {
        // Try to parse error from responseText
        let d = {};
        try { d = JSON.parse(responseText); } catch {}
        message.error((d as any).error || 'Failed to save');
      }
    } catch { message.error('Network error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Delete ${name}? This cannot be undone.`)) return;
    try {
      const r = await apiFetch(`${API_URL}/staff/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (r.ok) { message.success('Staff member deleted'); fetchStaff(); }
      else { message.error('Failed to delete'); }
    } catch { message.error('Network error'); }
  };

  const toggleActive = async (s: StaffMember) => {
    try {
      // Debug: log before API call
      console.log('Toggle active:', { url: `${API_URL}/staff/${s.id}`, isActive: !s.active });
      const r = await apiFetch(`${API_URL}/staff/${s.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ isActive: !s.active })
      });
      // Debug: log after response
      console.log('Toggle response status:', r.status);
      if (r.ok) { message.success(s.active ? 'Deactivated' : 'Activated'); fetchStaff(); }
      else { message.error('Failed to update'); }
    } catch { message.error('Network error'); }
  };

  // Resend Invite function
  const resendInvite = async (id: string, email: string, name: string) => {
    try {
      const r = await apiFetch(`${API_URL}/staff/${id}/resend-invite`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (r.ok) { message.success(`Invite resent to ${email}`); }
      else { message.error('Failed to resend invite'); }
    } catch { message.error('Network error'); }
  };

  const setWH = (day: string, field: string, val: any) =>
    setForm(f => ({ ...f, working_hours: { ...f.working_hours, [day]: { ...((f.working_hours as any)[day] || {}), [field]: val } } }));

  const filtered = staff.filter(s => {
    const q = search.toLowerCase();
    if (q && !s.name.toLowerCase().includes(q) && !s.email.toLowerCase().includes(q)) return false;
    if (roleFilter && s.role !== roleFilter) return false;
    if (statusFilter === 'active' && !s.active) return false;
    if (statusFilter === 'inactive' && (s.active || s.status === 'pending_verification')) return false;
    if (statusFilter === 'pending' && s.status !== 'pending_verification') return false;
    return true;
  });

  const { pages: staffPages, safePage: safeListPage, slice: paginatedStaff } =
    paginateList(filtered, listPage);

  const activeDays = (wh: any) =>
    wh ? DAYS.filter(d => wh[d]?.active).map(d => d.slice(0,3).toUpperCase()) : [];

  const activeCount = staff.filter(s => s.active).length;
  const pendingCount = staff.filter(s => s.status === 'pending_verification').length;

  const pageStyle = isLandlord
    ? {}
    : { minHeight: '100vh', background: '#f4f6f8', fontFamily: "'DM Sans', system-ui, sans-serif", padding: '32px 24px 60px', maxWidth: 1280, margin: '0 auto' };

  return (
    <div className={isLandlord ? 'sys-page' : undefined} style={pageStyle}>
      <style>{`
        ${isLandlord ? SYSTEM_PAGE_CSS : ''}
        @keyframes fadeIn { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
        @keyframes spin { to { transform:rotate(360deg); } }
        .s-card { animation: fadeIn 0.3s ease both; transition: transform 0.2s, box-shadow 0.2s; }
        .s-card:hover { transform: translateY(-3px); box-shadow: 0 8px 32px rgba(0,0,0,0.10) !important; }
        .s-btn:hover { filter: brightness(1.07); }
        .s-input:focus { outline: none; border-color: ${isLandlord ? '#2563eb' : '#6366f1'} !important; box-shadow: 0 0 0 3px ${isLandlord ? 'rgba(37,99,235,0.13)' : 'rgba(99,102,241,0.13)'}; }
        .s-select:focus { outline: none; border-color: ${isLandlord ? '#2563eb' : '#6366f1'}; }
      `}</style>

      {isLandlord ? (
        <>
          <div className="sys-banner-wrap sys-banner-staff">
            <div className="sys-banner-glass">
              <div>
                <h1>🧑‍💼 Staff</h1>
                <p>Invite agents, accountants &amp; maintenance staff — manage roles for {businessName}</p>
              </div>
              {canManageStaff && (
                <div className="sys-banner-actions">
                  <button type="button" className="sys-btn-white" onClick={openCreate}>+ Invite team member</button>
                </div>
              )}
            </div>
          </div>

          <div className="sys-kpi-row">
            <div className="sys-kpi sys-kpi-white"><div className="kl">Team size</div><div className="kv">{staff.length}</div></div>
            <div className="sys-kpi sys-kpi-blue"><div className="kl">Active</div><div className="kv">{activeCount}</div></div>
            <div className="sys-kpi sys-kpi-teal"><div className="kl">Pending invite</div><div className="kv">{pendingCount}</div></div>
            <div className="sys-kpi sys-kpi-white"><div className="kl">Roles</div><div className="kv">5</div></div>
          </div>

          <div className="sys-panel">
            <h3>Role permissions</h3>
            <div className="sys-role-grid">
              {LANDLORD_ROLE_PERMISSIONS.map(r => (
                <div key={r.role} className="sys-role-card">
                  <div className="role-name" style={{ color: r.color }}>{r.role}</div>
                  <div className="role-desc">{r.access}</div>
                </div>
              ))}
            </div>
          </div>

          {canManageStaff && (
            <div className="sys-panel">
              <h3>📧 Staff invite email preview</h3>
              <p style={{ margin: '0 0 12px', fontSize: 13, color: '#64748b' }}>
                When you invite someone, they receive this email mentioning <strong>{businessName}</strong> and a link to complete the invite form.
              </p>
              <div className="sys-invite-preview">{staffInvitePreview(businessName, form.name.trim() || 'New team member', userName || 'Your manager', businessEmail)}</div>
            </div>
          )}
        </>
      ) : (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
              <div style={{ width: 42, height: 42, borderRadius: 12, background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>👥</div>
              <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#22223b', letterSpacing: '-0.5px' }}>Team Members</h1>
            </div>
            <p style={{ margin: 0, fontSize: 14, color: '#64748b' }}>Manage your team and their booking availability</p>
          </div>
          {canManageStaff && (
            <button className="s-btn" onClick={openCreate} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 22px', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: 'white', border: 'none', borderRadius: 12, fontWeight: 700, fontSize: 14, cursor: 'pointer', boxShadow: '0 4px 16px rgba(99,102,241,0.4)' }}>
              + Add Member
            </button>
          )}
        </div>
      )}

      {!canManageStaff && (
        <div style={{ background: 'rgba(251,191,36,0.1)', color: '#fbbf24', padding: '12px 16px', borderRadius: 10, marginBottom: 20, fontSize: 14, border: '1px solid rgba(251,191,36,0.3)' }}>
          ⚠️ Only Admin or Manager can invite or manage staff members.
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 24, flexWrap: 'wrap', alignItems: 'center', background: '#f8fafc', padding: '14px 16px', borderRadius: 14, border: '1px solid #e5e7eb' }}>
        <input className="s-input" placeholder="🔍 Search name or email…" value={search} onChange={e => setSearch(e.target.value)}
          style={{ padding: '9px 13px', borderRadius: 9, border: '1px solid #e5e7eb', fontSize: 14, minWidth: 220, flex: 1, background: '#fff', color: '#22223b' }} />
        <select className="s-select" value={roleFilter} onChange={e => setRoleFilter(e.target.value)}
          style={{ padding: '9px 13px', borderRadius: 9, border: '1px solid #e5e7eb', fontSize: 14, background: '#fff', color: '#22223b' }}>
          <option value="">All roles</option>
          {['Admin','Staff','Editor','Viewer','Manager'].map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <select className="s-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          style={{ padding: '9px 13px', borderRadius: 9, border: '1px solid #e5e7eb', fontSize: 14, background: '#fff', color: '#22223b' }}>
          <option value="">Any status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="pending">⏳ Pending Invite</option>
        </select>
        {(search || roleFilter || statusFilter) && (
          <button onClick={() => { setSearch(''); setRoleFilter(''); setStatusFilter(''); }}
            style={{ padding: '9px 14px', borderRadius: 9, border: '1px solid #e5e7eb', background: '#f3f4f6', cursor: 'pointer', fontSize: 13, color: '#64748b' }}>
            ✕ Reset
          </button>
        )}
        <span style={{ marginLeft: 'auto', color: '#64748b', fontSize: 13, fontWeight: 600 }}>
          {filtered.length} member{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>

      <ListPagination
        total={staff.length}
        filtered={filtered.length}
        page={safeListPage}
        pages={staffPages}
        onPageChange={setListPage}
        noun="members"
      />

      {/* Grid */}
      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
          <div style={{ width: 38, height: 38, border: '4px solid #e5e7eb', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '64px 20px', background: '#ffffff', borderRadius: 18, border: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>👥</div>
          <div style={{ fontSize: 16, fontWeight: 600, color: '#6b7280', marginBottom: 8 }}>
            {staff.length === 0 ? 'No team members yet' : 'No staff match your filters'}
          </div>
          {staff.length === 0 && canManageStaff && (
            <button className="s-btn" onClick={openCreate} style={{ marginTop: 12, padding: '11px 22px', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: 'white', border: 'none', borderRadius: 10, fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
              Add your first team member →
            </button>
          )}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 20 }}>
          {paginatedStaff.map((s, idx) => {
            const rs = roleStyle(s.role);
            return (
              <div key={s.id} className="s-card" style={{ animationDelay: `${idx * 40}ms`, background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 4px 24px rgba(0,0,0,0.07)', border: `1px solid ${s.active ? '#e5e7eb' : '#fbbf24'}`, opacity: s.active ? 1 : 0.7, transition: 'box-shadow 0.2s, transform 0.2s' }}>
                {/* Avatar + name */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14 }}>
                  {s.photo_url ? (
                    <img src={s.photo_url} alt={s.name} style={{ width: 52, height: 52, borderRadius: '50%', objectFit: 'cover', border: '2px solid #e0e7ff', flexShrink: 0 }} />
                  ) : (
                    <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, color: 'white', fontWeight: 800, flexShrink: 0, boxShadow: '0 4px 12px rgba(99,102,241,0.13)' }}>
                      {s.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 15, color: '#22223b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.name}</div>
                    <div style={{ display: 'flex', gap: 6, marginTop: 5, flexWrap: 'wrap', alignItems: 'center' }}>
                      <span style={{ padding: '2px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: rs.bg, color: rs.color }}>{s.role}</span>
                      <span style={{ padding: '2px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: s.active ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)', color: s.active ? '#22c55e' : '#f87171' }}>
                        {s.active ? '● Active' : '○ Inactive'}
                      </span>
                      {s.status === 'pending_verification' && (
                        <span style={{ padding:'2px 9px', borderRadius:20, fontSize:11, fontWeight:700, background:'rgba(251,191,36,0.15)', color:'#fbbf24', marginLeft: 4 }}>
                          ⏳ Invite Sent
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Info */}
                <div style={{ fontSize: 13, color: '#64748b', display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 12, padding: '10px 12px', background: '#f8fafc', borderRadius: 9 }}>
                  <div style={{ color: '#94a3b8' }}>📧 <span style={{ color: '#cbd5e1' }}>{s.email}</span></div>
                  {s.phone && <div style={{ color: '#94a3b8' }}>📱 <span style={{ color: '#cbd5e1' }}>{s.phone}</span></div>}
                  {s.hourly_rate > 0 && <div style={{ color: '#94a3b8' }}>💷 <span style={{ color: '#a78bfa', fontWeight: 600 }}>£{s.hourly_rate}/hr</span></div>}
                </div>

                {/* Working days */}
                <div style={{ display: 'flex', gap: 4, marginBottom: 12, flexWrap: 'wrap' }}>
                  {DAYS.map(d => {
                    const active = s.working_hours?.[d]?.active;
                    return (
                      <span key={d} style={{ padding: '3px 7px', borderRadius: 6, fontSize: 10, fontWeight: 700, background: active ? 'rgba(99,102,241,0.2)' : '#f3f4f6', color: active ? '#818cf8' : '#9ca3af', border: `1px solid ${active ? 'rgba(99,102,241,0.4)' : '#e5e7eb'}` }}>
                        {d.slice(0, 3).toUpperCase()}
                      </span>
                    );
                  })}
                </div>

                {/* Skills */}
                {(s.skills || []).length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 12 }}>
                    {(s.skills || []).slice(0, 4).map((sk, i) => (
                      <span key={i} style={{ padding: '3px 8px', borderRadius: 20, fontSize: 11, background: '#ede9fe', color: '#7c3aed', border: '1px solid #ddd6fe' }}>{sk}</span>
                    ))}
                    {(s.skills || []).length > 4 && <span style={{ fontSize: 11, color: '#475569' }}>+{(s.skills || []).length - 4} more</span>}
                  </div>
                )}

                {/* Actions */}
                {canManageStaff && (
                  <div style={{ display: 'flex', gap: 8, paddingTop: 12, borderTop: '1px solid #f4f6f8' }}>
                    <button className="s-btn" onClick={() => openEdit(s)} style={{ flex: 1, padding: '8px 0', background: 'rgba(99,102,241,0.10)', color: '#6366f1', border: '1px solid #e0e7ff', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                      ✏️ Edit
                    </button>
                    <button className="s-btn" onClick={() => toggleActive(s)} style={{ flex: 1, padding: '8px 0', background: s.active ? 'rgba(251,191,36,0.10)' : 'rgba(34,197,94,0.10)', color: s.active ? '#fbbf24' : '#22c55e', border: `1px solid ${s.active ? '#fde68a' : '#bbf7d0'}`, borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                      {s.active ? '⏸ Deactivate' : '▶ Activate'}
                    </button>
                    {isAdmin && (
                      <>
                        <button className="s-btn" onClick={() => resendInvite(s.id, s.email, s.name)}
                          style={{ flex:1, padding:'8px 0', background:'rgba(251,191,36,0.12)', color:'#fbbf24', border:'1px solid rgba(251,191,36,0.3)', borderRadius:8, fontWeight:600, fontSize:13, cursor:'pointer' }}>
                          📧 Resend
                        </button>
                        <button className="s-btn" onClick={() => handleDelete(s.id, s.name)} style={{ padding: '8px 12px', background: 'rgba(239,68,68,0.10)', color: '#ef4444', border: '1px solid #fecaca', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                          🗑
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(6px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={() => setShowModal(false)}>
          <div style={{ background: '#ffffff', borderRadius: 18, padding: 28, maxWidth: 560, width: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 24px 80px rgba(0,0,0,0.06)', border: '1px solid #e5e7eb' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#111827' }}>{editId ? '✏️ Edit Member' : '➕ Add Team Member'}</h2>
              <button onClick={() => setShowModal(false)} style={{ background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 16, cursor: 'pointer', color: '#374151', padding: '4px 10px' }}>✕</button>
            </div>

            {/* Photo upload */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 18 }}>
              <div style={{ position: 'relative', flexShrink: 0 }}>
                {photoPreview
                  ? <img src={photoPreview} alt="preview" style={{ width: 72, height: 72, borderRadius: '50%', objectFit: 'cover', border: '2px solid #e0e7ff' }} />
                  : <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, color: 'white', fontWeight: 800 }}>
                      {form.name ? form.name.charAt(0).toUpperCase() : '?'}
                    </div>
                }
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#374151', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.6px' }}>Profile Photo</label>
                <label htmlFor="staff-photo-input" style={{ display: 'inline-block', padding: '7px 14px', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, cursor: 'pointer', color: '#374151', fontWeight: 500 }}>
                  📷 {photoPreview ? 'Change Photo' : 'Upload Photo'}
                </label>
                <input id="staff-photo-input" type="file" accept="image/*" style={{ display: 'none' }}
                  onChange={e => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onload = ev => {
                      const dataUrl = ev.target?.result as string;
                      setPhotoPreview(dataUrl);
                      // Strip prefix for backend (backend expects raw base64)
                      setPhotoBase64(dataUrl.split(',')[1] || dataUrl);
                    };
                    reader.readAsDataURL(file);
                  }}
                />
                {photoPreview && (
                  <button onClick={() => { setPhotoPreview(null); setPhotoBase64(null); }}
                    style={{ marginLeft: 8, background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 12 }}>
                    ✕ Remove
                  </button>
                )}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
              {([['Name *', 'name', 'Full name', 'text'], ['Email *', 'email', 'email@example.com', 'email']] as const).map(([l, f, p, t]) => (
                <div key={f}>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#374151', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.6px' }}>{l}</label>
                  <input className="s-input" value={(form as any)[f]} type={t} onChange={e => setForm(prev => ({ ...prev, [f]: e.target.value }))} placeholder={p}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 9, border: '1px solid #e5e7eb', fontSize: 14, boxSizing: 'border-box', background: '#ffffff', color: '#111827' }} />
                </div>
              ))}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.6px' }}>Phone</label>
                <input className="s-input" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+44 7700 000000"
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 9, border: '1px solid #e5e7eb', fontSize: 14, boxSizing: 'border-box', background: '#ffffff', color: '#111827' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.6px' }}>Role</label>
                <select className="s-select" value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 9, border: '1px solid #e5e7eb', fontSize: 14, background: '#ffffff', color: '#111827' }}>
                  {['Staff','Editor','Viewer','Manager'].map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.6px' }}>Hourly Rate (£)</label>
                <input className="s-input" value={form.hourly_rate} type="number" min={0} step={0.01} onChange={e => setForm(f => ({ ...f, hourly_rate: parseFloat(e.target.value) || 0 }))}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 9, border: '1px solid #e5e7eb', fontSize: 14, boxSizing: 'border-box', background: '#ffffff', color: '#111827' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.6px' }}>Skills (comma-separated)</label>
                <input className="s-input" value={form.skills} onChange={e => setForm(f => ({ ...f, skills: e.target.value }))} placeholder="Haircut, Styling…"
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 9, border: '1px solid #e5e7eb', fontSize: 14, boxSizing: 'border-box', background: '#ffffff', color: '#111827' }} />
              </div>
            </div>

            {/* Working Hours */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#374151', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.6px' }}>Working Hours</label>
              <div style={{ background: '#f8fafc', borderRadius: 10, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {DAYS.map(day => {
                  const wh = (form.working_hours as any)[day] || { active: false, start: '09:00', end: '17:00' };
                  return (
                    <div key={day} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <input type="checkbox" checked={!!wh.active} onChange={e => setWH(day, 'active', e.target.checked)} style={{ width: 15, height: 15, accentColor: '#6366f1', flexShrink: 0, cursor: 'pointer' }} />
                      <span style={{ width: 90, fontSize: 13, fontWeight: 600, textTransform: 'capitalize', color: wh.active ? '#111827' : '#9ca3af' }}>{day}</span>
                      {wh.active ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <input type="time" value={wh.start} onChange={e => setWH(day, 'start', e.target.value)} style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #e5e7eb', fontSize: 12, background: '#ffffff', color: '#111827' }} />
                          <span style={{ color: '#9ca3af', fontSize: 12 }}>→</span>
                          <input type="time" value={wh.end} onChange={e => setWH(day, 'end', e.target.value)} style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #e5e7eb', fontSize: 12, background: '#ffffff', color: '#111827' }} />
                        </div>
                      ) : <span style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>Closed</span>}
                    </div>
                  );
                })}
              </div>
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 22, cursor: 'pointer', fontSize: 14, fontWeight: 500, color: '#374151' }}>
              <input type="checkbox" checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} style={{ width: 15, height: 15, accentColor: '#6366f1' }} />
              Active — visible for bookings
            </label>

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowModal(false)} style={{ flex: 1, padding: 12, background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 10, fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
                Cancel
              </button>
              <button className="s-btn" onClick={handleSave} disabled={saving} style={{ flex: 2, padding: 12, background: saving ? '#e5e7eb' : 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: saving ? '#9ca3af' : 'white', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: saving ? 'not-allowed' : 'pointer' }}>
                {saving ? 'Saving…' : editId ? '💾 Update Member' : '➕ Add Member'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffManagement;
